import { Injectable, inject, computed, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map, catchError } from "rxjs/operators";
import { of, forkJoin, Observable } from "rxjs";
import { FormlyFieldConfig } from "@ngx-formly/core";

// API Services
import { RegionService } from "../../../api/services/region.service";
import { AreaOfInterestService } from "../../../api/services/area-of-interest.service";
import { EducationLevelService } from "../../../api/services/education-level.service";
import { MatchCompetencyService } from "../../../api/services/match-competency.service";

// API Models
import { Region } from "../../../api/models/region";
import { AreaOfInterest } from "../../../api/models/area-of-interest";
import { EducationLevel } from "../../../api/models/education-level";
import { MatchCompetency } from "../../../api/models/match-competency";
import { CompanyStatus } from "../../../api/models/company-status";

// Shared
import { targetGroupOptions } from "../../../shared/services/job.service";
import { LocaleService } from "../../../shared/services/locale.service";

/**
 * Service providing Formly field configurations for company admin forms.
 * Loads reference data and provides computed field configurations.
 */
@Injectable({ providedIn: "root" })
export class CompanyFormConfigService {
  private readonly regionService = inject(RegionService);
  private readonly areaOfInterestService = inject(AreaOfInterestService);
  private readonly educationLevelService = inject(EducationLevelService);
  private readonly matchCompetencyService = inject(MatchCompetencyService);
  private readonly localeService = inject(LocaleService);

  // Observable sources for reference data
  private readonly regions$ = this.regionService
    .getRegions()
    .pipe(catchError(() => of([])));

  private readonly areaOfInterests$ = this.areaOfInterestService
    .getAreaOfInterests()
    .pipe(
      map((response) => response?.data ?? []),
      catchError(() => of([]))
    );

  private readonly educationLevels$ = this.educationLevelService
    .getEducationLevels()
    .pipe(
      map((response) => response?.data ?? []),
      catchError(() => of([]))
    );

  private readonly matchCompetencies$ = this.matchCompetencyService
    .getMatchCompetencies()
    .pipe(
      map((response) => response?.data ?? []),
      catchError(() => of([]))
    );

  // Reference data as signals
  readonly regions: Signal<Region[]> = toSignal(this.regions$, {
    initialValue: [],
  });

  readonly areaOfInterests: Signal<AreaOfInterest[]> = toSignal(
    this.areaOfInterests$,
    { initialValue: [] }
  );

  readonly educationLevels: Signal<EducationLevel[]> = toSignal(
    this.educationLevels$,
    { initialValue: [] }
  );

  readonly matchCompetencies: Signal<MatchCompetency[]> = toSignal(
    this.matchCompetencies$,
    { initialValue: [] }
  );

  /**
   * Load all reference data needed for the form.
   * Call this and wait for completion before building fields.
   */
  loadReferenceData(): Observable<boolean> {
    return forkJoin([this.regions$, this.areaOfInterests$]).pipe(
      map(() => true),
      catchError(() => of(true))
    );
  }

  // Filtered competencies
  readonly companyCultureCompetencies = computed(() =>
    this.matchCompetencies().filter((c) => c.type === "CompanyCulture")
  );

  readonly personalityCompetencies = computed(() =>
    this.matchCompetencies().filter((c) => c.type === "Personality")
  );

  // Static options
  readonly companyStatusOptions = [
    { value: CompanyStatus.New, label: "New" },
    { value: CompanyStatus.Active, label: "Active" },
    { value: CompanyStatus.Inactive, label: "Inactive" },
  ];

  // Country options from LocaleService
  readonly countryOptions = computed(() =>
    this.localeService.availableCountries().map((c) => ({
      value: c.code,
      label: c.name,
    }))
  );

  /**
   * Build field configuration for the General tab.
   */
  buildGeneralFields(): FormlyFieldConfig[] {
    return [
      {
        wrappers: ["section-wrapper"],
        fieldGroup: [
          {
            key: "name",
            type: "form-input",
            props: { label: "Name", required: true },
          },
          {
            key: "registrationNumber",
            type: "form-input",
            props: { label: "Registration number" },
          },
          {
            key: "numberOfEmployees",
            type: "form-input",
            props: { label: "Number of employees", inputType: "number" },
          },
          {
            key: "status",
            type: "form-select",
            defaultValue: CompanyStatus.New,
            props: {
              label: "Status",
              options: this.companyStatusOptions,
            },
          },
          {
            key: "regions",
            type: "core-selector",
            props: {
              label: "Region",
              selectorType: "checkbox",
              options: this.regions(),
              selectorValue: "id",
              selectorLabel: "name",
            },
          },
          {
            key: "targetGroups",
            type: "tag-selector",
            props: {
              label: "Target groups",
              options: targetGroupOptions,
            },
          },
        ],
      },
      {
        wrappers: ["section-wrapper"],
        fieldGroup: [
          {
            key: "websiteUri",
            type: "form-input",
            props: { label: "Website URL", inputType: "url" },
          },
          {
            key: "linkedInUri",
            type: "form-input",
            props: { label: "LinkedIn URL", inputType: "url" },
          },
          {
            key: "googleUri",
            type: "form-input",
            props: { label: "Google URL", inputType: "url" },
          },
          {
            key: "twitterUri",
            type: "form-input",
            props: { label: "X (Twitter) URL", inputType: "url" },
          },
          {
            key: "facebookUri",
            type: "form-input",
            props: { label: "Facebook URL", inputType: "url" },
          },
          {
            key: "instagramUri",
            type: "form-input",
            props: { label: "Instagram URL", inputType: "url" },
          },
        ],
      },
      {
        key: "visitAddress",
        wrappers: ["section-wrapper"],
        props: { label: "Address" },
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            key: "street",
            type: "form-input",
            className: "col-sm-8",
            props: { label: "Street" },
          },
          {
            key: "number",
            type: "form-input",
            className: "col-sm-2",
            props: { label: "No." },
          },
          {
            key: "numberSuffix",
            type: "form-input",
            className: "col-sm-2",
            props: { label: "Addition" },
          },
          {
            key: "city",
            type: "form-input",
            className: "col-sm-8",
            props: { label: "City" },
          },
          {
            key: "postalCode",
            type: "form-input",
            className: "col-sm-4",
            props: { label: "Postal code" },
          },
          {
            key: "country",
            type: "form-select",
            className: "col-sm-12",
            props: {
              label: "Country",
              options: this.countryOptions(),
            },
          },
        ],
      },
    ];
  }

  /**
   * Build field configuration for the Match tab.
   */
  buildMatchFields(): FormlyFieldConfig[] {
    return [
      {
        key: "matchcriterion",
        type: "percentage-group",
        props: {
          label: "Weighting percentages",
          fields: [
            { key: "companyCultureFactor", label: "Company culture" },
            { key: "personalityFactor", label: "Personality" },
            { key: "areaOfInterestFactor", label: "Field of interest" },
          ],
        },
      },
      {
        key: "companyCultureCompetencies",
        type: "competency-picker",
        props: {
          label: "Company culture",
          maxSelections: 5,
          selectPrompt: "Select maximum 5 competences",
          sortPrompt: "Sort the selected competences based on priority",
          options: this.companyCultureCompetencies(),
        },
      },
      {
        key: "personalityCompetencies",
        type: "competency-picker",
        props: {
          label: "Personality",
          maxSelections: 2,
          selectPrompt: "Select maximum 2 competences",
          sortPrompt: "Sort the selected competences based on priority",
          options: this.personalityCompetencies(),
        },
      },
      {
        key: "areaOfInterests",
        type: "selector-areaofinterests",
        props: {
          label: "Area of interests",
          maxAmount: 15,
          data: this.areaOfInterests(),
        },
      },
      {
        key: "educationLevels",
        type: "education-level-selector",
        props: {
          label: "Education levels",
        },
      },
    ];
  }
}
